import * as Sentry from '@sentry/react';

/**
 * Sentry initialization. Off when `VITE_SENTRY_DSN_APP` is empty (default
 * for local dev) so we don't spam your free-tier 5K-error budget with
 * `npm run dev` noise. Production deploys set it via apps/app/.env.production.
 *
 * What we capture:
 *   - Unhandled errors + promise rejections (Sentry default), and nothing
 *     else: no Session Replay, no performance traces — see the note in
 *     init() for what those cost.
 *
 * What we scrub:
 *   - User email + display_name from breadcrumbs (PII rule, see CLAUDE.md)
 *   - Supabase auth tokens from URLs (the OAuth callback briefly has them)
 *   - localStorage values from breadcrumbs (anything with `sb-…-auth-token`)
 *
 * Source maps: uploaded automatically via @sentry/vite-plugin during the
 * production build when VITE_SENTRY_DSN_APP + SENTRY_AUTH_TOKEN are set.
 */

/**
 * The exact options we hand to Sentry.init. Exported so a test can assert the
 * shape without booting the SDK — see sentry.test.ts for why that matters.
 */
export function sentryOptions(dsn: string): Parameters<typeof Sentry.init>[0] {
  // Errors only — no Session Replay, no performance tracing. Measured
  // 2026-09-23: those two cost 253 KB raw / ~60 KB gzip of the first load,
  // more than the whole Georgian font, in exchange for 50 replays a month on
  // the free plan. Errors alone cost ~68 KB raw / ~20 KB gzip.
  //
  // Both are opt-in, so simply not listing them is enough. Do NOT reach for
  // `defaultIntegrations: false` or an `integrations` CALLBACK to "clean up":
  // those DO drop the defaults, and one of them, globalHandlers, is what
  // catches an uncaught error at all. (An `integrations` ARRAY is safe — the
  // SDK merges it with the defaults: @sentry/core getIntegrationsToSetup,
  // `[...defaultIntegrations, ...userIntegrations]`.)
  return {
    dsn,
    environment: (import.meta.env.MODE ?? 'production') as string,
    release: (import.meta.env.VITE_GIT_SHA as string | undefined) ?? undefined,
    sendDefaultPii: false,
    // Only errors thrown from our own bundle (/assets/*.js). Every issue in
    // the first two weeks except one came from code the browser or host app
    // injects into the page: Chrome on iPhone (frames named after the page
    // URL, e.g. /login:225), Facebook's in-app browser (iabjs://…). Not ours,
    // not fixable, and each one emails Tornike. Events with no stack keep
    // going through (Sentry only filters when it finds a URL).
    allowUrls: [/\/assets\/[^/?#]+\.js/],
    beforeSend,
  };
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN_APP as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      // Print once so we know Sentry is intentionally inert in dev.
      console.info('[sentry] disabled — VITE_SENTRY_DSN_APP not set');
    }
    return;
  }

  Sentry.init(sentryOptions(dsn));
}

/** Tag the current Sentry scope with the user's id only (no email/name). */
export function setSentryUser(userId: string | null): void {
  if (userId == null) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: userId });
}

/** Scrub PII before anything leaves the browser. */
function beforeSend(event: Parameters<NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend'] & object>[0]) {
  // Strip user email and IP — we identify by user_id (uuid) which is
  // useless to anyone reading Sentry without DB access.
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }
  // Sanitize breadcrumb URLs that might contain `?code=…` (OAuth) or
  // `#access_token=…` (implicit). Both are short-lived but still PII.
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b.data && typeof b.data['url'] === 'string') {
        b.data['url'] = scrubAuthFromUrl(b.data['url']);
      }
      if (typeof b.message === 'string') {
        b.message = scrubAuthFromUrl(b.message);
      }
    }
  }
  // Strip Supabase auth-token values from extra context.
  if (event.extra) {
    for (const k of Object.keys(event.extra)) {
      if (k.toLowerCase().includes('auth-token')) {
        event.extra[k] = '[redacted]';
      }
    }
  }
  return event;
}

function scrubAuthFromUrl(url: string): string {
  try {
    const u = new URL(url, 'https://placeholder.invalid');
    for (const key of ['code', 'access_token', 'refresh_token', 'id_token', 'state']) {
      if (u.searchParams.has(key)) u.searchParams.set(key, '[redacted]');
    }
    if (u.hash.includes('access_token=') || u.hash.includes('refresh_token=')) {
      u.hash = '#[redacted]';
    }
    return u.protocol === 'https:' && u.host !== 'placeholder.invalid'
      ? u.toString()
      : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}
